/* AcademyFooter — academy-only footer.
 * Demo credentials are NEVER shown publicly; admins can email them to a
 * specific person from the Admin → Academy → Demo Accounts panel. */
import React from 'react';
import { Link } from 'react-router-dom';
import {
  Mail, MapPin, Linkedin, Facebook, Instagram, Youtube, BookOpen,
} from 'lucide-react';
import ZheltaLogo from './ZheltaLogo';
import DivisionBridge from './DivisionBridge';

export default function AcademyFooter() {
  return (
    <footer
      className="text-zinc-800 bg-[#F5F5F7]"
    >

      {/* Brand + nav */}
      <section className="max-w-[1200px] mx-auto px-4 lg:px-6 py-12 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <Link to="/academy" className="inline-flex items-center gap-3 mb-4">
            <ZheltaLogo size={36} />
            <div className="leading-none">
              <div className="text-[18px] font-extrabold tracking-[0.2em]">ZHELTA</div>
              <div className="text-[10px] font-semibold tracking-[0.3em] text-[#f5a623]">ACADEMY</div>
            </div>
          </Link>
          <p className="text-[14px] text-zinc-600 leading-relaxed max-w-[480px]">
            Practical, expert-led education that prepares learners for in-demand technology and life-skill careers — taught by mentors, paced for working people.
          </p>
          <div className="mt-5 flex items-center gap-4 text-zinc-500">
            <a href="mailto:careers@zhelta.com" className="inline-flex items-center gap-1.5 hover:text-[#c9a227] text-[13px]">
              <Mail className="w-4 h-4" /> careers@zhelta.com
            </a>
            <span className="inline-flex items-center gap-1.5 text-[13px]">
              <MapPin className="w-4 h-4" /> New York, NY · USA
            </span>
          </div>
        </div>

        <div>
          <h4 className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#f5a623] mb-3">Programs</h4>
          <ul className="space-y-2 text-[13.5px] text-zinc-700">
            <li><Link to="/academy/programs" className="hover:text-[#c9a227]">Browse all programs</Link></li>
            <li><Link to="/academy/programs/it-certification-programs" className="hover:text-[#c9a227]">IT Certifications</Link></li>
            <li><Link to="/academy/programs/cybersecurity-awareness-best-practices" className="hover:text-[#c9a227]">Cybersecurity</Link></li>
            <li><Link to="/academy/programs/programming-software-development" className="hover:text-[#c9a227]">Programming</Link></li>
            <li><Link to="/academy/enroll" className="hover:text-[#c9a227]">Enroll now</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#f5a623] mb-3">Academy</h4>
          <ul className="space-y-2 text-[13.5px] text-zinc-700">
            <li><Link to="/academy" className="hover:text-[#c9a227]">Academy home</Link></li>
            <li><Link to="/academy/forgot-password" className="hover:text-[#c9a227]">Forgot password</Link></li>
            <li><Link to="/academy/support" className="hover:text-[#c9a227]">Technical support</Link></li>
            <li><a href="mailto:careers@zhelta.com" className="hover:text-[#c9a227]">Contact academy</a></li>
          </ul>
        </div>
      </section>

      {/* Bridge to other divisions */}
      <DivisionBridge current="academy" />

      <section className="border-t border-zinc-200">
        <div className="max-w-[1200px] mx-auto px-4 lg:px-6 py-5 flex items-center justify-between flex-wrap gap-3">
          <div className="text-[12px] text-zinc-500">
            <Link
              to="/copyright"
              data-testid="academy-footer-copyright"
              className="hover:text-[#c9a227] transition"
              title="Copyright & Terms of Use"
            >
              © {new Date().getFullYear()} ZHELTA Academy. Established 2012. Operating since 2024.
            </Link>
          </div>
          <div className="flex items-center gap-3 text-zinc-500">
            <Link
              to="/privacy"
              data-testid="academy-footer-privacy"
              className="text-[11.5px] hover:text-[#c9a227]"
            >
              Privacy policy
            </Link>
            <Link
              to="/cookies"
              data-testid="academy-footer-cookies"
              className="text-[11.5px] hover:text-[#c9a227]"
            >
              Cookies
            </Link>
            <a href="https://www.linkedin.com/company/zhelta-llc/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="hover:text-[#c9a227]"><Linkedin className="w-4 h-4" /></a>
            <a href="https://www.facebook.com/profile.php?id=61562259287313" target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="academy-footer-facebook" className="hover:text-[#c9a227]"><Facebook className="w-4 h-4" /></a>
            <a href="https://www.instagram.com/zhelta_support?igsh=dTdqbGo4MWZqcW1z&utm_source=qr" target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="academy-footer-instagram" className="hover:text-[#c9a227]"><Instagram className="w-4 h-4" /></a>
            <a href="https://youtube.com/@zheltallc?si=x-hOD-nWZpWempMK" target="_blank" rel="noopener noreferrer" aria-label="YouTube" data-testid="academy-footer-youtube" className="hover:text-[#c9a227]"><Youtube className="w-4 h-4" /></a>
            <Link to="/" className="text-[11.5px] hover:text-[#c9a227] inline-flex items-center gap-1">
              <BookOpen className="w-3 h-3" /> ZHELTA main site
            </Link>
          </div>
        </div>
      </section>
    </footer>
  );
}
